import { useCallback } from "react";
import type { ClipboardEvent } from "react";

interface UseDecimalSeparatorPasteProps {
  decimalSeparator: string;
  allowedDecimalSeparators: string[];
  onPaste?: (event: ClipboardEvent<HTMLDivElement>) => void;
}

// The last separator is the decimal one; any earlier ones are grouping and dropped
const normalizeDecimalSeparators = (text: string, decimalSeparator: string, allowedDecimalSeparators: string[]) => {
  const parts = allowedDecimalSeparators.reduce(
    (acc, separator) => acc.flatMap((part) => part.split(separator)),
    [text]
  );
  if (parts.length === 1) return text;

  return parts.slice(0, -1).join("") + decimalSeparator + parts[parts.length - 1];
};

/**
 * react-number-format only swaps alternate decimal separators on single-character edits,
 * so pasting "22.5" into a pt-BR field would report 225
 */
function useDecimalSeparatorPaste(props: UseDecimalSeparatorPasteProps) {
  const { decimalSeparator, allowedDecimalSeparators, onPaste } = props;

  return useCallback(
    (event: ClipboardEvent<HTMLDivElement>) => {
      onPaste?.(event);

      const input = event.target;
      if (event.defaultPrevented || !(input instanceof HTMLInputElement)) return;

      const text = event.clipboardData.getData("text");
      const normalized = normalizeDecimalSeparators(text, decimalSeparator, allowedDecimalSeparators);
      if (normalized === text) return;

      event.preventDefault();

      const { selectionStart, selectionEnd, value } = input;
      input.setRangeText(normalized, selectionStart ?? value.length, selectionEnd ?? value.length, "end");
      input.dispatchEvent(new Event("input", { bubbles: true }));
    },
    [decimalSeparator, allowedDecimalSeparators, onPaste]
  );
}

export default useDecimalSeparatorPaste;
